import { createFileRoute, Link } from "@tanstack/react-router";
import { useRef, useState, type FormEvent } from "react";
import { Trophy, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useServerFn } from "@tanstack/react-start";
import { requestLeagueAccess } from "@/lib/access.functions";
import { isValidName, NAME_HELPER_TEXT } from "@/lib/name-validation";
import { TURNSTILE_SITE_KEY } from "@/lib/turnstile";
import { toast } from "sonner";

export const Route = createFileRoute("/request-access")({
  component: RequestAccessPage,
  head: () => ({
    meta: [
      { title: "Request access — Bracket Goblin" },
      { name: "description", content: "Request access to a private bracket league with your invite code." },
      { property: "og:title", content: "Request access — Bracket Goblin" },
      { property: "og:description", content: "Request access to a private bracket league with your invite code." },
      { property: "og:url", content: "https://bracketgoblin.com/request-access" },
    ],
    links: [{ rel: "canonical", href: "https://bracketgoblin.com/request-access" }],
  }),
});

function RequestAccessPage() {
  const requestFn = useServerFn(requestLeagueAccess);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileFailed, setTurnstileFailed] = useState(false);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isValidName(firstName)) {
      return toast.error(`First name: ${NAME_HELPER_TEXT}`);
    }
    if (!isValidName(lastName)) {
      return toast.error(`Last name: ${NAME_HELPER_TEXT}`);
    }
    if (!turnstileToken) {
      return toast.error("Please complete the CAPTCHA challenge.");
    }
    setSubmitting(true);
    try {
      await requestFn({
        data: {
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          code: code.trim(),
          turnstileToken,
        },
      });
      setDone(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      toast.error(msg);
      // Tokens are single-use — reset so the user can try again.
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
        <div className="w-full max-w-sm text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-success/10">
            <CheckCircle2 className="h-7 w-7 text-success" />
          </div>
          <h1 className="font-display text-2xl font-bold text-foreground mb-2">Request submitted</h1>
          <p className="text-sm text-muted-foreground mb-6">
            If your code is valid, you&apos;ll receive an email shortly with next steps. First-time users will get a 6-digit activation code to set their password. If you already have an account, you can sign in now.
          </p>
          <div className="flex flex-col gap-3">
            <Button asChild>
              <Link to="/activate-account">
                Activate your account
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/" search={{ redirect: undefined }}>
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </Link>
            </Button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-2">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-[var(--shadow-neon)]">
            <Trophy className="h-6 w-6" strokeWidth={2.5} />
          </div>
          <h1 className="font-display text-2xl font-black uppercase tracking-tight text-foreground">
            Request access
          </h1>
          <p className="text-xs text-muted-foreground text-center">
            Enter the league code you were given to request access.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 rounded-xl border border-border bg-card p-6">
          <div className="space-y-1.5">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="firstName">First name</Label>
                <Input
                  id="firstName"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  maxLength={50}
                  className="border-primary"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lastName">Last name</Label>
                <Input
                  id="lastName"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  maxLength={50}
                  className="border-primary"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Shown to the league admin for approval. You'll set your league display name when you build your bracket.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="border-primary"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="code">League code</Label>
            <Input
              id="code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="FAMILY-X7K2A"
              className="font-mono uppercase border-primary"
              maxLength={32}
            />
          </div>

          <div className="flex flex-col items-center gap-2" style={{ minHeight: 65 }}>
            <Turnstile
              ref={turnstileRef}
              siteKey={TURNSTILE_SITE_KEY}
              options={{ theme: "dark", retry: "auto", refreshExpired: "auto" }}
              onSuccess={(token) => {
                setTurnstileToken(token);
                setTurnstileFailed(false);
              }}
              onExpire={() => setTurnstileToken(null)}
              onError={() => {
                setTurnstileToken(null);
                setTurnstileFailed(true);
              }}
            />
            {turnstileFailed ? (
              <p className="text-xs text-destructive text-center px-2">
                CAPTCHA couldn&apos;t load. Disable ad/privacy blockers for this site, or try a different browser. If the issue persists, email{" "}
                <a href="mailto:support@bracketgoblin.com" className="underline">support@bracketgoblin.com</a>.
              </p>
            ) : null}
          </div>

          <Button type="submit" className="w-full" disabled={submitting || !turnstileToken}>
            {submitting ? "Submitting…" : "Request access"}
          </Button>
          <div className="text-center text-xs">
            <Link to="/" search={{ redirect: undefined }} className="text-muted-foreground hover:text-foreground">
              Already have an account? Sign in
            </Link>
          </div>
        </form>
      </div>
    </main>
  );
}
